import React, { useEffect, useMemo, useState } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { ShieldOff, ShieldCheck, ShieldAlert, Radio, Plus, Search, Trash2, Bug, Zap, Gauge, Copy, RotateCcw, KeyRound, AlertTriangle } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import Modal from '../../components/admin/Modal';
import Paginacao from '../../components/admin/Paginacao';
import BloquearIpModal from '../../components/admin/BloquearIpModal';
import ConfirmarDesbloquearIpModal from '../../components/admin/ConfirmarDesbloquearIpModal';
import { ipsBloqueadosAPI } from '../../utils/adminSegurancaAPI';
import { useItensPorPagina } from '../../hooks/useItensPorPagina';

const SETE_DIAS_MS = 7 * 24 * 60 * 60 * 1000;
const ITENS_POR_PAGINA_MOBILE = 5;
const ITENS_POR_PAGINA_DESKTOP = 6;

const TIPO_META = {
  honeypot: { label: 'Honeypot', cor: 'bg-red-50 text-red-700', icon: Bug },
  preenchimento_rapido: { label: 'Preenchimento rápido', cor: 'bg-amber-50 text-amber-700', icon: Zap },
  rate_limit: { label: 'Rate limit', cor: 'bg-orange-50 text-orange-700', icon: Gauge },
  token_duplicado: { label: 'Token duplicado', cor: 'bg-purple-50 text-purple-700', icon: Copy },
  voto_duplicado: { label: 'Voto duplicado', cor: 'bg-purple-50 text-purple-700', icon: RotateCcw },
  login_falhou: { label: 'Login falhou', cor: 'bg-red-50 text-red-700', icon: KeyRound },
  ip_bloqueado_tentativa: { label: 'Tentativa de IP bloqueado', cor: 'bg-red-50 text-red-700', icon: ShieldOff }
};

function tempoRelativo(dataStr) {
  const diffMin = Math.floor((Date.now() - new Date(dataStr).getTime()) / 60000);
  if (diffMin < 1) return 'agora mesmo';
  if (diffMin < 60) return `há ${diffMin} min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `há ${diffH}h`;
  return `há ${Math.floor(diffH / 24)}d`;
}

function detalhesResumo(evento) {
  const d = evento.detalhes;
  if (!d) return null;
  if (d.email) return `tentou: ${d.email}`;
  if (d.enquete) return `enquete: ${d.enquete}`;
  if (d.fillTime !== undefined) return `${d.fillTime}ms (mínimo ${d.minFillTime}ms)`;
  return null;
}

const StatCard = ({ icon: Icone, cor, titulo, valor, delay }) => (
  <div
    className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-300 p-5 flex items-center gap-3.5 animate-fade-in-up"
    style={{ animationDelay: `${delay}s` }}
  >
    <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${cor}`}>
      <Icone className="w-5 h-5 text-white" />
    </div>
    <div className="min-w-0">
      <p className="text-2xl font-bold text-gray-900 leading-none">{valor}</p>
      <p className="text-xs font-medium text-gray-500 mt-1 leading-snug">{titulo}</p>
    </div>
  </div>
);

// Fora do componente pai de propósito — evita remontar todos os cards a
// cada tecla digitada na busca.
const BloqueadoCard = ({ ip, onDesbloquear, delay }) => (
  <div
    className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-300 p-5 flex flex-col gap-3 animate-fade-in-up"
    style={{ animationDelay: `${delay}s` }}
  >
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="font-mono font-semibold text-gray-900 truncate">{ip.ip}</p>
        {ip.motivo && <p className="text-sm text-gray-500 mt-0.5">{ip.motivo}</p>}
      </div>
      <ShieldOff className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
    </div>
    <p className="text-xs text-gray-400">
      bloqueado por {ip.bloqueado_por || 'admin'} em {new Date(ip.data_criacao).toLocaleString('pt-BR')}
    </p>
    <button
      onClick={() => onDesbloquear(ip)}
      className="flex items-center justify-center gap-1.5 text-sm font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg px-3 py-1.5 transition-colors self-start"
    >
      <ShieldCheck className="w-3.5 h-3.5" /> Desbloquear
    </button>
  </div>
);

const LivreCard = ({ ip, onBloquear, delay }) => (
  <div
    className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-300 p-5 flex flex-col gap-3 animate-fade-in-up"
    style={{ animationDelay: `${delay}s` }}
  >
    <div className="flex items-start justify-between gap-2">
      <p className="font-mono font-semibold text-gray-900 truncate">{ip.ip}</p>
      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 flex-shrink-0">
        {ip.total_requisicoes} req.
      </span>
    </div>
    <p className="text-xs text-gray-400">
      visto {tempoRelativo(ip.ultimo_acesso)} · desde {new Date(ip.primeiro_acesso).toLocaleDateString('pt-BR')}
    </p>
    <button
      onClick={() => onBloquear(ip.ip)}
      className="flex items-center justify-center gap-1.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg px-3 py-1.5 transition-colors self-start border border-red-100"
    >
      <Trash2 className="w-3.5 h-3.5" /> Bloquear
    </button>
  </div>
);

const EventoCard = ({ evento, onBloquear, delay }) => {
  const meta = TIPO_META[evento.tipo] || { label: evento.tipo, cor: 'bg-gray-100 text-gray-600', icon: AlertTriangle };
  const Icone = meta.icon;
  const resumo = detalhesResumo(evento);

  return (
    <div
      className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-300 p-5 flex flex-col gap-3 animate-fade-in-up"
      style={{ animationDelay: `${delay}s` }}
    >
      <div className="flex items-start justify-between gap-2">
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full flex items-center gap-1 ${meta.cor}`}>
          <Icone className="w-3 h-3" /> {meta.label}
        </span>
        <span className="text-xs text-gray-400 flex-shrink-0">{tempoRelativo(evento.data_criacao)}</span>
      </div>
      <p className="font-mono font-semibold text-gray-900 truncate">{evento.ip}</p>
      {resumo && <p className="text-sm text-gray-500 truncate">{resumo}</p>}
      {evento.rota && <p className="text-xs text-gray-400 truncate">{evento.rota}</p>}
      <button
        onClick={() => onBloquear(evento.ip)}
        className="flex items-center justify-center gap-1.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg px-3 py-1.5 transition-colors self-start border border-red-100"
      >
        <Trash2 className="w-3.5 h-3.5" /> Bloquear IP
      </button>
    </div>
  );
};

const IpsBloqueados = () => {
  const [bloqueados, setBloqueados] = useState([]);
  const [livres, setLivres] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aba, setAba] = useState('bloqueados'); // 'bloqueados'|'livres'|'eventos'
  const [busca, setBusca] = useState('');
  const [mostrarBloquear, setMostrarBloquear] = useState(false);
  const [ipParaBloquear, setIpParaBloquear] = useState(null);
  const [ipParaDesbloquear, setIpParaDesbloquear] = useState(null);
  const [pagina, setPagina] = useState(1);
  const itensPorPagina = useItensPorPagina(ITENS_POR_PAGINA_DESKTOP, ITENS_POR_PAGINA_MOBILE);

  const carregar = async () => {
    setLoading(true);
    try {
      const [listaBloqueados, listaLivres, respostaEventos] = await Promise.all([
        ipsBloqueadosAPI.listar(),
        ipsBloqueadosAPI.listarLivres(),
        ipsBloqueadosAPI.listarEventos()
      ]);
      setBloqueados(listaBloqueados);
      setLivres(listaLivres);
      setEventos(respostaEventos.eventos);
    } catch (error) {
      toast.error(error.userMessage || 'Erro ao carregar IPs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { carregar(); }, []);

  // Volta pra primeira página sempre que a aba, a busca, ou o tamanho de
  // página (troca desktop/mobile) mudam — senão o usuário pode ficar numa
  // página vazia sem entender por quê.
  useEffect(() => { setPagina(1); }, [aba, busca, itensPorPagina]);

  const bloqueadosFiltrados = useMemo(
    () => bloqueados.filter((ip) => ip.ip.includes(busca.trim())),
    [bloqueados, busca]
  );
  const livresFiltrados = useMemo(
    () => livres.filter((ip) => ip.ip.includes(busca.trim())),
    [livres, busca]
  );
  const eventosFiltrados = useMemo(
    () => eventos.filter((e) => e.ip.includes(busca.trim())),
    [eventos, busca]
  );

  const bloqueiosSemana = bloqueados.filter((ip) => Date.now() - new Date(ip.data_criacao).getTime() <= SETE_DIAS_MS).length;

  const abrirBloquearComIp = (ip) => setIpParaBloquear(ip);
  const fecharModalBloquear = () => {
    setMostrarBloquear(false);
    setIpParaBloquear(null);
  };

  const listaAtual = aba === 'bloqueados' ? bloqueadosFiltrados : aba === 'livres' ? livresFiltrados : eventosFiltrados;
  const mensagemVazia = {
    bloqueados: 'Nenhum IP bloqueado.',
    livres: 'Nenhum IP livre visto nos últimos 7 dias.',
    eventos: 'Nenhum evento suspeito nos últimos 7 dias.'
  }[aba];

  const totalPaginas = Math.max(1, Math.ceil(listaAtual.length / itensPorPagina));
  const listaPaginada = listaAtual.slice((pagina - 1) * itensPorPagina, pagina * itensPorPagina);

  return (
    <AdminLayout title="Segurança">
      <Toaster position="top-center" />

      <div className="flex justify-between items-start gap-4 mb-6">
        <p className="text-gray-500 max-w-lg">Endereços com acesso negado às páginas públicas e ao login, e quem apareceu recentemente sem estar bloqueado.</p>
        <button onClick={() => setMostrarBloquear(true)} className="flex items-center gap-2 flex-shrink-0 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg px-4 py-2.5 transition-colors">
          <Plus className="w-4 h-4" /> Bloquear IP
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={ShieldOff} cor="bg-red-500" titulo="IPs bloqueados" valor={bloqueados.length} delay={0} />
        <StatCard icon={ShieldAlert} cor="bg-amber-500" titulo="Bloqueios nos últimos 7 dias" valor={bloqueiosSemana} delay={0.05} />
        <StatCard icon={Radio} cor="bg-[#40A1E6]" titulo="IPs livres monitorados" valor={livres.length} delay={0.1} />
        <StatCard icon={AlertTriangle} cor="bg-purple-500" titulo="Eventos suspeitos (7 dias)" valor={eventos.length} delay={0.15} />
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
        <div className="inline-flex items-center gap-1 bg-gray-100 rounded-xl p-1 flex-shrink-0">
          <button
            onClick={() => setAba('bloqueados')}
            className={`flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-lg transition-colors ${
              aba === 'bloqueados' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Bloqueados
            <span className="text-xs font-medium px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">{bloqueadosFiltrados.length}</span>
          </button>
          <button
            onClick={() => setAba('livres')}
            className={`flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-lg transition-colors ${
              aba === 'livres' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Livres
            <span className="text-xs font-medium px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">{livresFiltrados.length}</span>
          </button>
          <button
            onClick={() => setAba('eventos')}
            className={`flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-lg transition-colors ${
              aba === 'eventos' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Eventos
            <span className="text-xs font-medium px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">{eventosFiltrados.length}</span>
          </button>
        </div>

        <div className="relative flex-1 min-w-[160px]">
          <Search className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar por IP..."
            className="w-full rounded-lg border border-gray-200 pl-9 pr-3 py-2 text-sm font-mono focus:border-[#40A1E6] focus:ring-2 focus:ring-[#40A1E6]/15 outline-none transition-all"
          />
        </div>
      </div>

      {loading ? (
        <div className="loading-spinner mx-auto" />
      ) : listaAtual.length === 0 ? (
        <p className="text-gray-500 text-center py-12">{mensagemVazia}</p>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {aba === 'bloqueados' &&
              listaPaginada.map((ip, index) => (
                <BloqueadoCard key={ip.id} ip={ip} onDesbloquear={setIpParaDesbloquear} delay={Math.min(index * 0.04, 0.4)} />
              ))}
            {aba === 'livres' &&
              listaPaginada.map((ip, index) => (
                <LivreCard key={ip.ip} ip={ip} onBloquear={abrirBloquearComIp} delay={Math.min(index * 0.04, 0.4)} />
              ))}
            {aba === 'eventos' &&
              listaPaginada.map((evento, index) => (
                <EventoCard key={evento.id} evento={evento} onBloquear={abrirBloquearComIp} delay={Math.min(index * 0.04, 0.4)} />
              ))}
          </div>

          <Paginacao
            pagina={pagina}
            totalPaginas={totalPaginas}
            onMudarPagina={setPagina}
            total={listaAtual.length}
            itensPorPagina={itensPorPagina}
          />
        </>
      )}

      {(mostrarBloquear || ipParaBloquear) && (
        <Modal titulo="Bloquear IP" onFechar={fecharModalBloquear} maxWidth="max-w-md">
          <BloquearIpModal ipInicial={ipParaBloquear} onFechar={fecharModalBloquear} onBloqueado={carregar} />
        </Modal>
      )}

      {ipParaDesbloquear && (
        <Modal titulo="Desbloquear IP" onFechar={() => setIpParaDesbloquear(null)} maxWidth="max-w-md">
          <ConfirmarDesbloquearIpModal ip={ipParaDesbloquear} onFechar={() => setIpParaDesbloquear(null)} onConfirmado={carregar} />
        </Modal>
      )}
    </AdminLayout>
  );
};

export default IpsBloqueados;
